import { io, type Socket } from 'socket.io-client'
import { consola } from 'consola'

const logger = consola.withTag('support-socket')

/**
 * Синглтон socket.io-соединения чата поддержки.
 *
 * - транспорт только websocket (контракт с бэком: polling между репликами Swarm не живёт)
 * - путь /api/v2/socket.io на том же хосте, что и REST (config.public.baseUrl)
 * - токен уходит в handshake.auth и читается ЗАНОВО на каждой попытке коннекта:
 *   клиентский access-токен живёт 5 минут, фоновый рефреш держит localStorage свежим,
 *   поэтому reconnect после обрыва/просрочки сам подхватывает новый токен
 * - отказ аутентификации приходит штатным connect_error — socket.io сам ретраит
 *   с бэкоффом, к этому моменту токен обычно уже обновлён
 *
 * Состояние модуля — вне компонентов (обычные ref на уровне модуля), чтобы не ловить
 * detached-scope синглтон-композаблов: слушатели навешиваются на socket, не на scope.
 */

type TokenSource = 'user' | 'admin'

const socketRef = ref<Socket | null>(null)
const isConnected = ref(false)
let currentSource: TokenSource | null = null

function readToken(source: TokenSource): string | null {
  if (import.meta.server) return null
  return localStorage.getItem(source === 'admin' ? 'adminAccessToken' : 'authAccessToken')
}

export function useSupportSocket() {
  const config = useRuntimeConfig()

  /**
   * Подключиться (или переключить роль user↔admin). Идемпотентно: живое соединение
   * той же роли переиспользуется.
   */
  function connect(source: TokenSource): Socket | null {
    if (import.meta.server) return null
    if (!readToken(source)) {
      logger.warn('Нет токена для подключения к чату поддержки', { source })
      return null
    }

    if (socketRef.value && currentSource === source) return socketRef.value
    if (socketRef.value) disconnect()

    const socket = io(String(config.public.baseUrl), {
      path: '/api/v2/socket.io',
      transports: ['websocket'],
      // Функция — токен перечитывается на каждой попытке (ре)коннекта
      auth: (setAuth) => setAuth({ token: readToken(source) }),
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15_000,
    })

    socket.on('connect', () => {
      isConnected.value = true
      logger.info('Соединение установлено', { source })
    })
    socket.on('disconnect', (reason) => {
      isConnected.value = false
      logger.info('Соединение закрыто', { reason })
    })
    socket.on('connect_error', (error) => {
      isConnected.value = false
      logger.warn('Ошибка подключения (ретрай со свежим токеном)', { message: error.message })
    })

    socketRef.value = socket
    currentSource = source
    return socket
  }

  function disconnect(): void {
    if (!socketRef.value) return
    socketRef.value.removeAllListeners()
    socketRef.value.disconnect()
    socketRef.value = null
    currentSource = null
    isConnected.value = false
  }

  /** Подписка на событие; возвращает функцию отписки (для onUnmounted компонента) */
  function on<T = unknown>(event: string, handler: (payload: T) => void): () => void {
    socketRef.value?.on(event, handler as never)
    return () => socketRef.value?.off(event, handler as never)
  }

  return { connect, disconnect, on, isConnected, socket: socketRef }
}
